'use client';

import { forwardRef, useEffect, useRef, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import { ILocalizedParams } from '../../models/ICommon';
// import LeftArrow from './svg/LeftArrow';
// import MenuArrows from './svg/MenuArrows';
import { twMerge } from 'tailwind-merge';
import { localizedMonths } from '../../utils/date';
import DatePickerIcon from './svg/DatePickerIcon';
import { MenuSelect } from './MenuSelect';
import styles from '@/styles/datepicker.module.scss';


interface Props {
  field: 'fromDate' | 'toDate';
  state: Partial<{ fromDate: Date; toDate: Date; }>;
  handleChange: (date: Date) => void;
  className?: string;
}

const DatePicker = forwardRef(function Inner({ locale = 'ar', className, state, field, handleChange }: ILocalizedParams<Props>, ref: any) {
  const months: string[] = localizedMonths[locale];
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 15 }, (_, i) => currentYear - i);
  const isFrom = field === 'fromDate';

  return (
    <div dir='ltr' className={twMerge(styles['datepicker'], 'absolute top-[105%] left-0 w-full z-10 h-fit shadow-datepicker min-h-[285px] tablet:min-h-[unset] bg-transparent', className)} ref={ref}>

      <ReactDatePicker
      calendarClassName={twMerge('!rounded-[inherit]')}
        renderCustomHeader={({
          date,
          changeMonth,
          changeYear,
        }) => (
          <div className='flex items-center justify-between w-full'>
            <div className='flex items-center w-full justify-center gap-x-[8px] [&>*]:h-[36px] [&>*]:flex-1 [&>*]:flex-shrink-0'>
              <MenuSelect
                selected={months[new Date(date).getMonth()]}
                options={months.map((o) => ({ title: o, value: o }))}
                onChange={(value) => changeMonth(months.indexOf(String(value)))}
                placeholder={months[new Date(date).getMonth()]}
                className={{ 
                  wrapper: 'gap-0 flex-[5] w-full select-sm dark:bg-night-300 dark:[&>*]:text-white', 
                  menu: { wrapper: 'grid-cols-1 z-10', item: 'md' } 
                }}
              />

              <MenuSelect
                selected={years.find(y => y === new Date(date).getFullYear())}
                options={years.map((o) => ({ title: String(o), value: o }))}
                onChange={(value) => changeYear(years[years.indexOf(Number(value))])}
                placeholder={years[years.indexOf(currentYear)].toString()}
                className={{ 
                  wrapper: 'gap-0 flex-[4] w-full select-sm dark:bg-night-300 dark:[&>*]:text-white', 
                  menu: { wrapper: 'grid-cols-1 z-10', item: 'md' } 
                }}
              />
            </div>
          </div>
        )}
        {
          ...(isFrom ? {
            selectsStart: true,
            maxDate: state['toDate'] ? new Date(state['toDate'])  : null
          } : {
            selectsEnd: true,
            minDate: state['fromDate'] ? new Date(state['fromDate'])  : null
          })
        }
        selected={state[field] ? new Date(state[field]!) :  null}
        startDate={isFrom ? null : (state['fromDate'] ? new Date(state['fromDate'])  : null)}
        endDate={isFrom ? (state['toDate'] ? new Date(state['toDate'])  : null) : null}
        // selectsRange
        inline
        fixedHeight
        onChange={handleChange}
        // dateFormat={'dd-MM-yyyy'}
      />
    </div>
  );
});


interface DatePickerCompProps {
  placeholder: string;
  field: 'fromDate' | 'toDate';
  state: Partial<{ fromDate: Date; toDate: Date; }>;
  handleDate: (field: 'fromDate' | 'toDate', date: Date) => void;
}


export default function DatePickerComp({ field, state, handleDate, placeholder }: DatePickerCompProps) {
  const [expanded, setExpanded] = useState(false);

  const pickerRef = useRef<any>();
  const containerRef = useRef<any>();

  const handleClick = (e: MouseEvent) => {
    const { current: picker } = pickerRef;
    const { current: container } = containerRef;
    if (
      (container?.contains(e.target) || container?.contains(picker)) &&
      (picker ? !picker.contains(e.target) : true)
    ) setExpanded(prevState => !prevState);
  }

  useEffect(() => {
    window.addEventListener('mousedown', handleClick);
    return () => {
      window.removeEventListener('mousedown', handleClick);
    }
  }, []);


  const getFormattedTitle = () => {
    let result = placeholder;
    const date = state[field];
    if (date) {
      const parsed = new Date(date);
      const day = parsed.getDate();
      const month = parsed.getMonth() + 1;
      const year = parsed.getFullYear();
      result = `${day}/${month}/${year}`;
    }
    return result;
  }

  return (
    <div ref={containerRef} className='flex items-center justify-between p-[12px] h-[40px] bg-gray-0 dark:bg-quinary cursor-pointer'>
      <span className='md '>{getFormattedTitle()}</span>
      <DatePickerIcon />
      {expanded ? <DatePicker locale='ar'
        key={String(expanded)}
        ref={pickerRef}
        state={state}
        field={field}
        handleChange={(date: Date) => {
          handleDate(field,date);
            setExpanded(false)
        }} /> : null}
      {/* className={twMerge('hidden', expanded && 'block')} */}
    </div>
  )
}

